import { useRef } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import GradientIcon from "@mui/icons-material/Gradient";
import RectangleIcon from "@mui/icons-material/Rectangle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { AddButtonsRow, LayerRow, LayersList } from "../style";
import useLanguage from "../../../hooks/useLanguage";

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) || 0;
  const g = parseInt(h.slice(2, 4), 16) || 0;
  const b = parseInt(h.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${alpha ?? 1})`;
}

function buildCssGradient(stops) {
  if (!stops || stops.length < 2) return "#888";
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const parts = sorted.map((s) => `${hexToRgba(s.color, s.alpha ?? 1)} ${Math.round(s.position * 100)}%`);
  return `linear-gradient(90deg, ${parts.join(", ")})`;
}

function LayerPreview({ el }) {
  if (el.type === "text") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <TextFieldsIcon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
        <Typography
          variant="caption"
          noWrap
          sx={{ color: "text.secondary", fontStyle: "italic", fontSize: 11, maxWidth: 80 }}
        >
          {(el.defaultText || "").slice(0, 20) || el.label || "Text"}
        </Typography>
      </Box>
    );
  }

  if (el.type === "logo") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {el._previewUrl ? (
          <Box
            component="img"
            src={el._previewUrl}
            alt=""
            sx={{ width: 22, height: 22, objectFit: "cover", borderRadius: 0.5, flexShrink: 0 }}
          />
        ) : (
          <Box sx={{ width: 22, height: 22, borderRadius: 0.5, bgcolor: "action.disabledBackground", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ImageIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          </Box>
        )}
      </Box>
    );
  }

  if (el.type === "gradient") {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 0.5,
          background: buildCssGradient(el.stops),
          border: "1px solid",
          borderColor: "borderColor.dark",
          flexShrink: 0,
        }}
      />
    );
  }

  if (el.type === "shape") {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 0.5,
          background: hexToRgba(el.fill || "#000000", el.alpha ?? 1),
          border: "1px solid",
          borderColor: "borderColor.dark",
          flexShrink: 0,
        }}
      />
    );
  }

  return null;
}

export default function LayersPanel({ elements, selectedId, dispatch }) {
  const [{ pages }] = useLanguage();
  const tl = (pages.overlayTemplates || {}).layers || {};
  const dragRef = useRef(null);

  const handleDragStart = (e, id) => {
    dragRef.current = id;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (targetId) => {
    if (!dragRef.current || dragRef.current === targetId) return;
    dispatch({ type: "REORDER", fromId: dragRef.current, toId: targetId });
    dragRef.current = null;
  };

  const sorted = [...elements].sort((a, b) => b.z - a.z);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <AddButtonsRow>
        <Tooltip title={tl.addText || "Add Text"}>
          <IconButton size="small" onClick={() => dispatch({ type: "ADD", elementType: "text" })}>
            <TextFieldsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={tl.addLogo || "Add Logo"}>
          <IconButton size="small" onClick={() => dispatch({ type: "ADD", elementType: "logo" })}>
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={tl.addGradient || "Add Gradient"}>
          <IconButton size="small" onClick={() => dispatch({ type: "ADD", elementType: "gradient" })}>
            <GradientIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={tl.addShape || "Add Shape"}>
          <IconButton size="small" onClick={() => dispatch({ type: "ADD", elementType: "shape" })}>
            <RectangleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </AddButtonsRow>

      <Typography variant="caption" sx={{ px: 1, pt: 1, color: "text.secondary" }}>
        {tl.header || "Layers"}
      </Typography>

      <LayersList>
        {sorted.map((el) => (
          <LayerRow
            key={el.id}
            selected={el.id === selectedId ? "true" : undefined}
            onClick={() => dispatch({ type: "SELECT", id: el.id })}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(el.id)}
          >
            <Box
              draggable
              onDragStart={(e) => handleDragStart(e, el.id)}
              sx={{ display: "flex", cursor: "grab", color: "text.secondary", flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>

            <LayerPreview el={el} />

            <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: 12 }}>
              {el.label || el.type}
            </Typography>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: "TOGGLE_VISIBLE", id: el.id });
              }}
            >
              {el._hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: "REMOVE", id: el.id });
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </LayerRow>
        ))}
        {elements.length === 0 && (
          <Typography variant="caption" sx={{ p: 1, color: "text.secondary", display: "block" }}>
            {tl.empty || "No layers yet"}
          </Typography>
        )}
      </LayersList>
    </Box>
  );
}
